import { notification } from 'ant-design-vue'
import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

export type ReportCommentDraftKind = 'new-thread' | 'reply'
export type CommentDraftSubmitter = (content: string) => Promise<boolean> | boolean

type ReportCommentDraftKeyOptions = {
  userId: string
  organisationId: string
  reportId: string
  kind: ReportCommentDraftKind
  rootThreadId?: string
}

type UseReportCommentDraftOptions = {
  userId: MaybeRefOrGetter<string | undefined>
  organisationId: MaybeRefOrGetter<string | undefined>
  reportId: MaybeRefOrGetter<string | undefined>
  kind: ReportCommentDraftKind
  rootThreadId?: MaybeRefOrGetter<string | undefined>
}

const reportCommentDraftStoragePrefix = 'ruminati_report_comment_draft_v1:'

export function getReportCommentDraftStorageKey(options: ReportCommentDraftKeyOptions): string {
  return `${reportCommentDraftStoragePrefix}${encodeURIComponent(JSON.stringify(options))}`
}

function getSessionStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined

  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

export function readReportCommentDraft(key: string): string | undefined {
  try {
    return getSessionStorage()?.getItem(key) ?? undefined
  } catch {
    return undefined
  }
}

export function writeReportCommentDraft(key: string, content: string): void {
  try {
    const storage = getSessionStorage()
    if (!storage) return

    if (content === '') storage.removeItem(key)
    else storage.setItem(key, content)
  } catch {
    // Draft persistence is best effort and must not interrupt editing.
  }
}

export async function submitCommentDraft(
  content: string,
  onSubmit: CommentDraftSubmitter,
  onSuccess: () => void,
): Promise<void> {
  try {
    const submitted = await onSubmit(content)
    if (submitted !== false) onSuccess()
  } catch {
    notification.error({
      message: 'Unable to submit comment',
      description: 'Please try again.',
    })
  }
}

export function clearPersistedReportCommentDrafts(): void {
  try {
    const storage = getSessionStorage()
    if (!storage) return

    const keysToRemove: Array<string> = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(reportCommentDraftStoragePrefix)) keysToRemove.push(key)
    }
    keysToRemove.forEach((key) => storage.removeItem(key))
  } catch {
    // Draft persistence is best effort and must not interrupt session cleanup.
  }
}

export function useReportCommentDraft(options: UseReportCommentDraftOptions) {
  const draft = ref('')
  const storageKey = computed(() => {
    const userId = toValue(options.userId)
    const organisationId = toValue(options.organisationId)
    const reportId = toValue(options.reportId)
    const rootThreadId = options.rootThreadId ? toValue(options.rootThreadId) : undefined

    if (!userId || !organisationId || !reportId) return undefined
    if (options.kind === 'reply' && !rootThreadId) return undefined

    return getReportCommentDraftStorageKey({
      userId,
      organisationId,
      reportId,
      kind: options.kind,
      ...(rootThreadId ? { rootThreadId } : {}),
    })
  })

  watch(
    storageKey,
    (key) => {
      draft.value = key ? (readReportCommentDraft(key) ?? '') : ''
    },
    { immediate: true },
  )

  function setDraft(content: string): void {
    draft.value = content
    const key = storageKey.value
    if (key) writeReportCommentDraft(key, content)
  }

  function clearDraft(key = storageKey.value): void {
    if (!key) return
    writeReportCommentDraft(key, '')
    if (key === storageKey.value) draft.value = ''
  }

  return {
    draft,
    storageKey,
    setDraft,
    clearDraft,
  }
}
